/**
 * ChangeAnalysisView — the 5th comparison view mode (F7 + F8 / T4).
 *
 * Renders the change-analysis results as a self-contained surface, driven by
 * GET /comparisons/{id}/change-importance + change-summary:
 *
 *   工具栏 (run trigger + importance distribution)
 *   降级条 (F7 partial-success / F8 degraded — conditional)
 *   修订摘要面板 (F8, always shown — carries its own empty copy)
 *   重要度卡片列表 (F7, sorted substantive → unclear → wording)
 *
 * Importance node ids come from the materialized working tree, which the diff
 * views don't share, so this view does not anchor badges into the document DOM —
 * each card carries its own change excerpt + drill-down context. Mirrors F6-6.
 */
import { useMemo } from 'react';

import type { ImportanceResult, VeriDocRole } from '../../lib/veridoc-api';

import { ChangeAnalysisEmpty } from './ChangeAnalysisEmpty';
import { ChangeAnalysisToolbar } from './ChangeAnalysisToolbar';
import { ChangeSummaryPanel } from './ChangeSummaryPanel';
import { IMPORTANCE_SEVERITY, normalizeImportance } from './constants';
import { ImportanceCard } from './ImportanceCard';
import { useChangeAnalysis } from './useChangeAnalysis';

interface ChangeAnalysisViewProps {
  comparisonId: string;
  userRole: VeriDocRole | null;
  active: boolean;
  onError: (message: string) => void;
}

function sortByImportance(results: ImportanceResult[]): ImportanceResult[] {
  return [...results].sort(
    (a, b) =>
      IMPORTANCE_SEVERITY[normalizeImportance(a.payload.importance)] -
      IMPORTANCE_SEVERITY[normalizeImportance(b.payload.importance)],
  );
}

export function ChangeAnalysisView({
  comparisonId,
  userRole,
  active,
  onError,
}: ChangeAnalysisViewProps): React.JSX.Element {
  const canRun = userRole === 'lawyer' || userRole === 'reviewer';
  const analysis = useChangeAnalysis({
    comparisonId,
    enabled: active,
    canRun,
    onError,
  });

  const sorted = useMemo(
    () => sortByImportance(analysis.results),
    [analysis.results],
  );

  // Cross-session truth: the comparison has been analyzed once a summary exists
  // (generatedAt) OR a run completed this session.
  const hasAnalyzed =
    analysis.summary?.generatedAt != null || analysis.ranThisSession;

  const importanceFailedCount = analysis.lastRun?.failedCount ?? 0;
  const summaryDegraded = analysis.summary?.degraded ?? false;
  const showDegradeBanner = importanceFailedCount > 0 || summaryDegraded;

  return (
    <div className="flex flex-col gap-4">
      <ChangeAnalysisToolbar
        results={analysis.results}
        degradedCount={importanceFailedCount}
        running={analysis.running}
        canRun={canRun}
        hasAnalyzed={hasAnalyzed}
        onRun={() => void analysis.run()}
      />

      {showDegradeBanner && (
        <div className="rounded-md border border-semantic-warning bg-semantic-warningBg px-4 py-3">
          <p className="text-sm font-semibold text-semantic-warning">
            部分分析因 LLM 不可达而降级
          </p>
          <p className="mt-1 text-xs text-ink-700">
            {importanceFailedCount > 0 &&
              `${importanceFailedCount} 处变更未能分类。`}
            {summaryDegraded && '修订摘要未能生成。'}
            已成功的结论不受影响，配置可用的 LLM 服务后重新分析即可补全。
          </p>
        </div>
      )}

      <Body
        status={analysis.status}
        summary={analysis.summary}
        results={sorted}
        running={analysis.running}
        canRun={canRun}
        hasAnalyzed={hasAnalyzed}
        onRetry={() => void analysis.refresh()}
      />
    </div>
  );
}

function Body({
  status,
  summary,
  results,
  running,
  canRun,
  hasAnalyzed,
  onRetry,
}: {
  status: ReturnType<typeof useChangeAnalysis>['status'];
  summary: ReturnType<typeof useChangeAnalysis>['summary'];
  results: ImportanceResult[];
  running: boolean;
  canRun: boolean;
  hasAnalyzed: boolean;
  onRetry: () => void;
}): React.JSX.Element {
  if (status === 'loading' || status === 'idle') {
    return <Centered text="加载变更分析…" />;
  }
  if (status === 'error') {
    return (
      <div className="flex flex-col items-center gap-3 rounded-lg border border-ink-200 bg-ink-0 px-6 py-12 text-center shadow-sm">
        <p className="text-sm text-ink-500">加载变更分析结果失败。</p>
        <button
          type="button"
          onClick={onRetry}
          className="rounded-sm border border-ink-200 bg-ink-50 px-3 py-1.5 text-xs text-ink-600 hover:bg-ink-100 hover:text-ink-900"
        >
          重试
        </button>
      </div>
    );
  }
  return (
    <>
      <ChangeSummaryPanel summary={summary} />
      {results.length === 0 ? (
        <ChangeAnalysisEmpty
          canRun={canRun}
          running={running}
          hasAnalyzed={hasAnalyzed}
        />
      ) : (
        <ul className="flex flex-col gap-3">
          {results.map((result) => (
            <ImportanceCard key={result.id} result={result} />
          ))}
        </ul>
      )}
    </>
  );
}

function Centered({ text }: { text: string }): React.JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-[40vh] items-center justify-center text-sm text-ink-500"
    >
      {text}
    </div>
  );
}

export default ChangeAnalysisView;
